'use client'

import React, { useState } from 'react'
import type { BeforeAfterComparisonBlock } from '@/payload-types'
import { Media } from '@/components/Media'
import { cn } from '@/utilities/cn'

type Props = {
  className?: string
} & BeforeAfterComparisonBlock

const BeforeAfterSlider: React.FC<{
  beforeImage: any
  afterImage: any
  title?: string | null
}> = ({ beforeImage, afterImage, title }) => {
  const [sliderPosition, setSliderPosition] = useState(50)
  const [isDragging, setIsDragging] = useState(false)

  const handleMove = (clientX: number, rect: DOMRect) => {
    const x = clientX - rect.left
    const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100)
    setSliderPosition(percentage)
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return
    const rect = e.currentTarget.getBoundingClientRect()
    handleMove(e.clientX, rect)
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return
    const rect = e.currentTarget.getBoundingClientRect()
    handleMove(e.touches[0].clientX, rect)
  }

  return (
    <div className="space-y-4">
      <div
        className="relative w-full aspect-[4/3] overflow-hidden rounded-2xl shadow-lg cursor-ew-resize select-none"
        onMouseDown={() => setIsDragging(true)}
        onMouseUp={() => setIsDragging(false)}
        onMouseLeave={() => setIsDragging(false)}
        onMouseMove={handleMouseMove}
        onTouchStart={() => setIsDragging(true)}
        onTouchEnd={() => setIsDragging(false)}
        onTouchMove={handleTouchMove}
      >
        {/* After Image (background) */}
        <div className="absolute inset-0">
          <Media resource={afterImage} imgClassName="w-full h-full object-cover" />
          <div className="absolute bottom-4 right-4 bg-[#0066CC] text-white px-4 py-2 rounded-full text-sm font-semibold">
            Efter
          </div>
        </div>

        {/* Before Image (overlay) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <Media resource={beforeImage} imgClassName="w-full h-full object-cover" />
          <div className="absolute bottom-4 left-4 bg-[#211F54] text-white px-4 py-2 rounded-full text-sm font-semibold">
            Före
          </div>
        </div>

        {/* Slider Line */}
        <div
          className="absolute inset-y-0 w-1 bg-white shadow-lg"
          style={{ left: `${sliderPosition}%` }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-[#0066CC]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </div>
        </div>
      </div>
      {title && (
        <p className="text-center text-gray-700 font-medium">{title}</p>
      )}
    </div>
  )
}

export const BeforeAfterComparisonBlockComponent: React.FC<Props> = ({
  heading,
  description,
  comparisons,
  layout = 'slider',
  className,
}) => {
  if (!comparisons || comparisons.length === 0) return null

  return (
    <section className={cn('py-16 lg:py-24 bg-white', className)}>
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#211F54] mb-6">
              {heading}
            </h2>
            {description && (
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                {description}
              </p>
            )}
          </div>

          {/* Comparisons */}
          {layout === 'slider' && (
            <div className="space-y-12">
              {comparisons.map((comparison, index) => (
                <div key={index}>
                  <BeforeAfterSlider
                    beforeImage={comparison.beforeImage}
                    afterImage={comparison.afterImage}
                    title={comparison.title}
                  />
                  {comparison.description && (
                    <p className="text-center text-gray-600 mt-4 max-w-2xl mx-auto">
                      {comparison.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {layout === 'sideBySide' && (
            <div className="space-y-12">
              {comparisons.map((comparison, index) => (
                <div key={index} className="space-y-4">
                  {comparison.title && (
                    <h3 className="text-2xl font-bold text-[#211F54] text-center">
                      {comparison.title}
                    </h3>
                  )}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="relative rounded-2xl overflow-hidden shadow-lg">
                      <Media resource={comparison.beforeImage} imgClassName="w-full h-full object-cover aspect-[4/3]" />
                      <div className="absolute bottom-4 left-4 bg-[#211F54] text-white px-4 py-2 rounded-full text-sm font-semibold">
                        Före
                      </div>
                    </div>
                    <div className="relative rounded-2xl overflow-hidden shadow-lg">
                      <Media resource={comparison.afterImage} imgClassName="w-full h-full object-cover aspect-[4/3]" />
                      <div className="absolute bottom-4 right-4 bg-[#0066CC] text-white px-4 py-2 rounded-full text-sm font-semibold">
                        Efter
                      </div>
                    </div>
                  </div>
                  {comparison.description && (
                    <p className="text-center text-gray-600 max-w-2xl mx-auto">
                      {comparison.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {layout === 'grid' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {comparisons.map((comparison, index) => (
                <div key={index} className="space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="relative rounded-lg overflow-hidden shadow">
                      <Media resource={comparison.beforeImage} imgClassName="w-full h-full object-cover aspect-square" />
                      <div className="absolute bottom-2 left-2 bg-[#211F54] text-white px-2 py-1 rounded text-xs font-semibold">
                        Före
                      </div>
                    </div>
                    <div className="relative rounded-lg overflow-hidden shadow">
                      <Media resource={comparison.afterImage} imgClassName="w-full h-full object-cover aspect-square" />
                      <div className="absolute bottom-2 right-2 bg-[#0066CC] text-white px-2 py-1 rounded text-xs font-semibold">
                        Efter
                      </div>
                    </div>
                  </div>
                  {comparison.title && (
                    <p className="text-center text-gray-700 font-medium text-sm">
                      {comparison.title}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
